import React, { useState } from "react";
import "./AccountSettings.css";
import { useNavigate } from "react-router-dom";
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";
import { supabase } from "../../supabase/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";

const CardRow = ({ icon, title, subtitle, color, onClick }) => {
  return (
    <div className="as-card-row" onClick={onClick} role="button" tabIndex={0}>
      <div className="as-card-icon" style={{ backgroundColor: color }}>
        {icon}
      </div>
      <div className="as-card-text">
        <div className="as-card-title">{title}</div>
        <div className="as-card-sub">{subtitle}</div>
      </div>
      <div className="as-card-chevron">›</div>
    </div>
  );
};

const AccountSettings = () => {
  const navigate = useNavigate();
  const { translations: t } = useLanguage();
  const { currentUser, userProfile } = useAuth();

  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmailDisplay, setResetEmailDisplay] = useState("");
  const [resetError, setResetError] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleReset = async () => {
    try {
      let email = currentUser?.email || "";
      if (!email) {
        email = window.prompt(
          t.accountSettings_resetPrompt || "Enter your account email to receive a reset link:",
          ""
        );
        if (!email) return;
      }
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) throw error;
      
      setResetEmailDisplay(email.trim());
      setResetError("");
      setShowResetModal(true);
    } catch (err) {
      console.error("Failed to send reset email:", err);
      setResetError(
        t.accountSettings_resetFailed || "Failed to send reset email. Please verify your email and try again."
      );
      setShowResetModal(true);
    }
  };

  const handleDelete = async () => {
    try {
      if (!currentUser) {
        alert(t.accountSettings_deleteMustLogin || "You must be logged in to delete your account.");
        return;
      }
      setShowDeleteDialog(true);
    } catch (error) {
      console.error("Failed to open delete dialog:", error);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      if (!currentUser) return;
      setDeleting(true);
      const uid = currentUser.id;

      // Delete user data from Supabase tables
      const tablesToClean = [
        { table: "posts", field: "posted_by_id" },
        { table: "saves", field: "user_id" },
        { table: "post_likes", field: "user_id" },
      ];

      // Delete data where user is the owner/creator
      for (const { table, field } of tablesToClean) {
        try {
          const { error } = await supabase
            .from(table)
            .delete()
            .eq(field, uid);
          
          if (error) {
            console.warn(`Error deleting from ${table}:`, error);
          }
        } catch (e) {
          console.warn(`Skipping cleanup for table ${table}:`, e);
        }
      }

      // Handle follows table separately since user can be both follower and following
      try {
        const { error: followerError } = await supabase
          .from("follows")
          .delete()
          .or(`follower_id.eq.${uid},following_id.eq.${uid}`);
        if (followerError) {
          console.warn("Error deleting follows:", followerError);
        }
      } catch (e) {
        console.warn("Skipping follows cleanup:", e);
      }

      // Try to delete from other tables if they exist
      const optionalTables = [
        { table: "products", field: "user_id" },
        { table: "transactions", field: "user_id" },
        { table: "conversations", field: "user_id" },
      ];

      for (const { table, field } of optionalTables) {
        try {
          const { error } = await supabase
            .from(table)
            .delete()
            .eq(field, uid);
          if (error && error.code !== 'PGRST116') { // PGRST116 = table doesn't exist
            console.warn(`Error deleting from ${table}:`, error);
          }
        } catch (e) {
          // Table might not exist, ignore
        }
      }

      // Delete user profile from profiles table
      try {
        const { error } = await supabase
          .from("profiles")
          .delete()
          .eq("user_id", uid);
        if (error) {
          console.warn("Error deleting profile:", error);
        }
      } catch (e) {
        console.warn("Error deleting profile:", e);
      }

      // Sign out the user (auth user deletion requires admin privileges or edge function)
      try {
        await supabase.auth.signOut();
      } catch (e) {
        console.warn("Error signing out:", e);
      }

      alert(t.accountSettings_deleteSuccess || "Your account and related data have been deleted.");
      navigate("/auth");
    } catch (error) {
      console.error("Failed to delete account:", error);
      alert(t.accountSettings_deleteFailed || "Failed to delete account. Please try again later.");
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  return (
    <div className="as-container">
      <FloatingSidebarButton userProfile={userProfile} />
      <div className="as-header">
        <button
          className="as-back"
          onClick={() => navigate(-1)}
          aria-label={t.back || "Back"}
          title={t.back || "Back"}
        >
          <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>
        <h1 className="as-title">{t.accountSettings || "Account Settings"}</h1>
      </div>

      <div className="as-card">
        <CardRow
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 11H7a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2Z" stroke="#0ea5e9" strokeWidth="2"/>
              <path d="M12 3a4 4 0 0 0-4 4v4h8V7a4 4 0 0 0-4-4Z" stroke="#0ea5e9" strokeWidth="2"/>
            </svg>
          }
          title={t.accountSettings_resetPassword?.title || "Reset Password"}
          subtitle={t.accountSettings_resetPassword?.subtitle || "Send password reset email to your account"}
          color="#e0f2fe"
          onClick={handleReset}
        />

        <CardRow
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6H5H21" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M19 6V20C19 20.5523 18.5523 21 18 21H6C5.44772 21 5 20.5523 5 20V6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 11V17" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 11V17" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title={t.accountSettings_deleteAccount?.title || "Delete Account"}
          subtitle={t.accountSettings_deleteAccount?.subtitle || "Permanently delete your account and data"}
          color="#fee2e2"
          onClick={handleDelete}
        />
      </div>

      {showResetModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowResetModal(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              width: 'min(92vw, 440px)',
              padding: 20,
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: resetError ? '#fee2e2' : '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {resetError ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 9v4" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M12 17h.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" stroke="#ef4444" strokeWidth="2"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 11H7a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2Z" stroke="#0ea5e9" strokeWidth="2"/>
                    <path d="M12 3a4 4 0 0 0-4 4v4h8V7a4 4 0 0 0-4-4Z" stroke="#0ea5e9" strokeWidth="2"/>
                  </svg>
                )}
              </div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {resetError
                  ? (t.accountSettings_modal_failedTitle || 'Could not send reset email')
                  : (t.accountSettings_modal_sentTitle || 'Reset email sent')}
              </div>
            </div>

            <div style={{ color: '#4b5563', fontSize: 14, lineHeight: 1.6 }}>
              {resetError
                ? resetError
                : (
                  (t.accountSettings_modal_sentBody &&
                    t.accountSettings_modal_sentBody.replace("{email}", resetEmailDisplay)) ||
                  `We sent a link to reset your password to ${resetEmailDisplay}. Please check your inbox and follow the instructions.`
                )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                onClick={() => setShowResetModal(false)}
                style={{
                  background: '#111827',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '10px 14px',
                  cursor: 'pointer'
                }}
              >
                {t.close || "Close"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeleteDialog && (
        <div
          className="account-settings-delete-dialog"
          role="dialog"
          aria-modal="true"
          onClick={() => !deleting && setShowDeleteDialog(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
        >
          <div
            className="confirmation-dialog"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#111827',
              color: '#e5e7eb',
              borderRadius: 16,
              width: 'min(92vw, 440px)',
              padding: 20,
              boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
              border: '1px solid rgba(255,255,255,0.08)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: 'rgba(239,68,68,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ef4444'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9v4" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M12 17h.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" stroke="#ef4444" strokeWidth="2"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>
                  {t.accountSettings_deleteAccount?.title || 'Delete Account'}
                </div>
                <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 6 }}>
                  {t.accountSettings_deleteConfirm || 'Are you sure you want to permanently delete your account and all related data? This action cannot be undone.'}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
              <button
                onClick={() => !deleting && setShowDeleteDialog(false)}
                disabled={deleting}
                style={{
                  background: 'transparent',
                  color: '#e5e7eb',
                  border: '1px solid rgba(255,255,255,0.14)',
                  borderRadius: 10,
                  padding: '10px 14px',
                  fontWeight: 600,
                  cursor: deleting ? 'not-allowed' : 'pointer'
                }}
              >
                {t.cancel || 'Cancel'}
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                style={{
                  background: 'var(--primary-color, #2563eb)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '10px 14px',
                  fontWeight: 700,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  boxShadow: '0 8px 20px rgba(37,99,235,0.45)',
                  opacity: deleting ? 0.85 : 1
                }}
              >
                {deleting ? (t.processing || 'Processing...') : (t.confirm || 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;
